// src/modules/requerimiento/utils/reporteModem.js
// ¿El módem está reportando? Misma lógica que la app Android (EstadoReporte): se busca el
// equipo en las plataformas (las mismas consultas que el panel de plataformas del Centro de
// equipos) y el último reporte es el MÁS RECIENTE entre todas.
import {
  searchFlespiPrimary,
  searchFlespiSecondary,
  searchLeasecontrolUnits,
  searchWialonUnits,
} from "../../../services/modems";
import { buscarVehiculoGlobal } from "../../vehiculos/services/vehiculosService";

/** Desde cuántos días sin datos un "reporta" se marca como dudoso. */
export const DIAS_REPORTE_VIEJO = 2;

const OFFSET_LA_PAZ = "-04:00"; // Bolivia no tiene horario de verano

/**
 * Fecha de reporte -> ms. Llegan como unix (s o ms), "yyyy-MM-dd HH:mm[:ss]" (hora de
 * Bolivia), ISO con zona o "dd/MM/yyyy HH:mm[:ss]". Lo que no se entiende -> null.
 */
export function parsearFechaReporte(valor) {
  if (valor == null || valor === "" || valor === "-") return null;
  if (typeof valor === "number" || /^\d{9,13}$/.test(String(valor).trim())) {
    const n = Number(valor);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 1e12 ? n * 1000 : n;
  }
  const t = String(valor).trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (m) return Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || "00"}${OFFSET_LA_PAZ}`) || null;
  m = t.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (m) return Date.parse(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:${m[6] || "00"}${OFFSET_LA_PAZ}`) || null;
  if (/\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/.test(t)) return Date.parse(t) || null;
  return null;
}

/** ms -> "2026-10-14 08:30" en hora de Bolivia. */
export function formatearFechaReporte(ms) {
  if (!ms) return "";
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/La_Paz", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(ms)).map((x) => [x.type, x.value])
  );
  return `${p.year}-${p.month}-${p.day} ${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
}

/** "hoy" / "ayer" / "hace N días". */
export function haceDias(dias) {
  if (dias == null) return "";
  if (dias === 0) return "hoy";
  if (dias === 1) return "ayer";
  return `hace ${dias} días`;
}

/**
 * Consulta las plataformas del módem. Devuelve:
 *  { estado: "reporta" | "no_reporta" | "no_se_pudo",
 *    plataformas: [{ titulo, ultimoMs }], ultimoMs, dias, desactualizado }
 */
export async function verificarReporte({ num_sim, emai } = {}) {
  const valores = [num_sim, emai].map((v) => String(v ?? "").trim()).filter(Boolean);
  if (!valores.length) return { estado: "no_se_pudo", plataformas: [] };

  const [f1, f2, wi, le, gl] = await Promise.allSettled([
    searchFlespiPrimary(valores),
    searchFlespiSecondary(valores),
    searchWialonUnits(valores),
    searchLeasecontrolUnits(valores),
    buscarVehiculoGlobal(valores[0], valores[1]),
  ]);
  const todas = [f1, f2, wi, le, gl];
  if (todas.every((r) => r.status === "rejected")) return { estado: "no_se_pudo", plataformas: [] };

  const ok = (r) => (r.status === "fulfilled" ? r.value : null);
  const flespi1 = ok(f1)?.data?.[0];
  const flespi2 = ok(f2)?.data?.[0];
  const wialon = ok(wi)?.data?.data?.[0];
  const leasecontrol = ok(le)?.data?.data?.[0];
  const globales = ok(gl)?.data || [];
  const porFuente = (fuente) =>
    (Array.isArray(globales) ? globales : []).find((v) => String(v?.fuente || "").toLowerCase() === fuente.toLowerCase());

  const candidatas = [
    ["Flespi 1", flespi1, flespi1?.telemetry?.timestamp],
    ["Flespi 2", flespi2, flespi2?.telemetry?.["server.timestamp"]],
    ["Wialon", wialon, wialon?.lmsg?.t],
    ["LeaseControl", leasecontrol, leasecontrol?.lmsg?.t],
    ["TRS Rastreo", porFuente("rastreo_bo"), porFuente("rastreo_bo")?.fecha_ultimo_registro],
    ["YPFB Nacional", porFuente("YPFB nacional"), porFuente("YPFB nacional")?.fecha_ultimo_registro],
    ["YPFB Internacional", porFuente("YPFB internacional"), porFuente("YPFB internacional")?.fecha_ultimo_registro],
  ];
  const plataformas = candidatas
    .filter(([, dato]) => !!dato)
    .map(([titulo, , fecha]) => ({ titulo, ultimoMs: parsearFechaReporte(fecha) }));

  if (!plataformas.length) return { estado: "no_reporta", plataformas };

  const ultimoMs = Math.max(0, ...plataformas.map((p) => p.ultimoMs || 0)) || null;
  const dias = ultimoMs ? Math.max(0, Math.floor((Date.now() - ultimoMs) / 86400000)) : null;
  return {
    estado: "reporta",
    plataformas,
    ultimoMs,
    dias,
    desactualizado: (dias ?? 0) >= DIAS_REPORTE_VIEJO,
  };
}
